/* How a bonus's value field is interpreted.
 *
 * A bonus is usually worth a figure — "597" renders as $597 and counts toward
 * the total. But some of the strongest bonuses don't have a number on them
 * ("Priceless", "Included for life"), and forcing one either invents a price or
 * leaves the slot empty. So the field takes either: type a number and it's
 * money, type anything else and it's a label shown verbatim, with no dollar
 * sign and no contribution to the total.
 *
 * CommonJS on purpose — scripts/bake-checkout-config.js requires this from
 * plain node at build time, and the admin editor imports it through webpack.
 *
 * functions/index.js carries its own copy of this rule: it deploys as a
 * separate package and cannot reach src/. The two must agree, because this
 * decides what the buyer sees and the server is what gets to say so.
 */

// Whether the row is crossed out, which the title carries as `~~markdown~~`.
const { isBonusStruck } = require("./bonus-markdown");

// Returns one of:
//   { kind: "none" }                  nothing to show in the value slot
//   { kind: "number", value: 597 }    money — render as $597, add to the total
//   { kind: "label",  value: "..." }  verbatim text, no $, not in the total
function parseBonusValue(raw) {
  const text = String(raw == null ? "" : raw).trim();
  if (!text) return { kind: "none" };

  // Tolerate the ways a price actually gets typed. Without this, "1,200" and
  // "$1,200" both fail the number test and would silently render as bare text
  // labels — the same keystrokes that used to produce a price would quietly
  // stop producing one.
  const numeric = text.replace(/^\$/, "").replace(/,/g, "").trim();
  const n = Number(numeric);

  if (numeric !== "" && Number.isFinite(n)) {
    // A real number. Only a positive figure earns a price, which is the rule
    // that was already in force — 0 and negatives show nothing rather than
    // becoming a literal "0" in the gold row.
    return n > 0 ? { kind: "number", value: n } : { kind: "none" };
  }

  return { kind: "label", value: text };
}

// The figure as the page writes it: "$1,200".
function formatBonusMoney(value) {
  return `$${Number(value).toLocaleString("en-MY")}`;
}

// Sum of the numeric bonuses only. Labels are worth something to the buyer but
// not something we can add up, so they sit outside the total rather than being
// guessed at.
//
// Struck-out bonuses are out too. The foot line this feeds says the total is
// "included at no extra cost", and a row the page has just crossed out is the
// one thing that isn't — counting it would put a number on the page that the
// page itself contradicts two lines above.
function totalBonusValue(bonuses) {
  return (Array.isArray(bonuses) ? bonuses : []).reduce((sum, b) => {
    if (isBonusStruck(b)) return sum;
    const parsed = parseBonusValue(b && b.value);
    return sum + (parsed.kind === "number" ? parsed.value : 0);
  }, 0);
}

// Labels are admin-entered text sitting in a narrow row; long ones would push
// the bonus name out. Enforced server-side too.
const BONUS_LABEL_MAX = 40;

module.exports = {
  parseBonusValue,
  formatBonusMoney,
  totalBonusValue,
  BONUS_LABEL_MAX,
};
